/**
 * "You may also like" inside the cart drawer and on the cart page (F-25).
 *
 * Asks Shopify's own recommendations endpoint for products related to what is
 * in the cart, and renders a small row of real products with an Add button.
 * Nothing here is invented: when Shopify returns no recommendations the
 * component renders nothing at all, rather than filling the space with filler.
 *
 * Why this is not Dawn's <product-recommendations>: that element is built for a
 * single product on a product page. A cart holds several, and its contents
 * change under the component, so this one re-asks whenever the cart changes.
 *
 * Adding goes through Shopify's cart API, then re-renders the drawer/cart with
 * the same Section Rendering call Dawn uses, so totals and the header count
 * update together and the added product appears as a normal cart line. Products
 * already in the cart are filtered out.
 *
 * Client feedback 30 Oct 2026: the row is horizontal and swipeable (arrows on
 * desktop), and a product with options is chosen right in its card: a select
 * lists only that product's available colour/size variants, and Add stays
 * disabled until one is picked, so nothing is ever added on a guess.
 */
// Wrapped and guarded: the file is loaded by the layout and again by the cart
// page's section, and a classic script run twice must not redeclare the class.
(() => {
if (customElements.get('squish-cart-suggestions')) return;

class SquishCartSuggestions extends HTMLElement {
  connectedCallback() {
    this.limit = Number(this.dataset.limit) || 4;
    this.addEventListener('click', (e) => this.onClick(e));
    this.addEventListener('change', (e) => this.onChange(e));
    this.load();
  }

  /** The anchor product is the most recently added line, falling back to the first. */
  async anchorProductId() {
    const res = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, { headers: { Accept: 'application/json' } });
    const cart = await res.json();
    this.inCartIds = new Set(cart.items.map((i) => i.product_id));
    return cart.items[0]?.product_id ?? null;
  }

  async load() {
    try {
      const productId = await this.anchorProductId();
      if (!productId) return this.render([]);

      const root = window.Shopify?.routes?.root || '/';
      const res = await fetch(`${root}recommendations/products.json?product_id=${productId}&limit=${this.limit + 4}&intent=related`);
      if (!res.ok) return this.render([]);
      const { products } = await res.json();

      this.render(
        (products || [])
          .filter((p) => p.available && !this.inCartIds.has(p.id))
          .slice(0, this.limit)
      );
    } catch {
      // A failed suggestion request must never get in the way of checking out.
      this.render([]);
    }
  }

  /** The store's own money format, so these prices read like the rest of the theme. */
  money(cents) {
    if (window.SquishMoney?.format) return window.SquishMoney.format(cents, { whole: true });
    const currency = window.Shopify?.currency?.active || 'INR';
    return new Intl.NumberFormat('en-IN', { style: 'currency', currency, maximumFractionDigits: 2 }).format(cents / 100);
  }

  render(products) {
    if (products.length === 0) {
      this.innerHTML = '';
      this.hidden = true;
      return;
    }
    this.hidden = false;

    const heading = this.dataset.heading || 'You may also like';
    const arrow = (d) => `<svg viewBox="0 0 14 14" aria-hidden="true" focusable="false"><path d="${d}" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
    this.innerHTML = `
      <div class="sq-sug__top">
        <h3 class="sq-sug__heading">${this.escape(heading)}</h3>
        ${products.length > 1 ? `<div class="sq-sug__nav">
          <button type="button" data-scroll="-1" aria-label="Previous suggestions">${arrow('M9 2 4 7l5 5')}</button>
          <button type="button" data-scroll="1" aria-label="More suggestions">${arrow('m5 2 5 5-5 5')}</button>
        </div>` : ''}
      </div>
      <ul class="sq-sug__list" role="list">
        ${products
          .map((p) => {
            const image = p.featured_image
              ? `<img src="${this.escape(p.featured_image)}&width=120" alt="" width="52" height="62" loading="lazy">`
              : '';
            const available = p.variants.filter((v) => v.available);
            const single = p.variants.length === 1;
            const selectId = `SqSug-${p.id}`;
            // Only real, available variants are offered; the first option is a
            // prompt, so the shopper always makes the choice.
            const pick = single
              ? `<button type="button" class="sq-sug__add" data-variant="${p.variants[0].id}" aria-label="Add ${this.escape(p.title)} to cart">Add</button>`
              : `<label class="visually-hidden" for="${selectId}">Colour and size for ${this.escape(p.title)}</label>
                 <select id="${selectId}" data-pick>
                   <option value="">Select ${this.escape(p.options.map((o) => (o.name || o).toString().toLowerCase()).join(' / '))}</option>
                   ${available.map((v) => `<option value="${v.id}">${this.escape(v.title)}</option>`).join('')}
                 </select>
                 <button type="button" class="sq-sug__add" data-add aria-disabled="true" aria-label="Add ${this.escape(p.title)} to cart">Add</button>`;
            return `
              <li class="sq-sug__item">
                <a class="sq-sug__media" href="${this.escape(p.url)}" tabindex="-1" aria-hidden="true">${image}</a>
                <div class="sq-sug__body">
                  <a class="sq-sug__title" href="${this.escape(p.url)}">${this.escape(p.title)}</a>
                  <span class="sq-sug__price">${p.price_varies ? 'From ' : ''}${this.money(p.price)}</span>
                </div>
                <div class="sq-sug__pick">${pick}</div>
              </li>`;
          })
          .join('')}
      </ul>`;
    this.updateNav();
    this.querySelector('.sq-sug__list')?.addEventListener('scroll', () => this.updateNav(), { passive: true });
  }

  /** Arrow buttons page the row by one card; disabled at either end. */
  updateNav() {
    const list = this.querySelector('.sq-sug__list');
    const [prev, next] = this.querySelectorAll('[data-scroll]');
    if (!list || !prev) return;
    prev.disabled = list.scrollLeft <= 2;
    next.disabled = list.scrollLeft + list.clientWidth >= list.scrollWidth - 2;
  }

  onChange(e) {
    const select = e.target.closest('select[data-pick]');
    if (!select) return;
    const add = select.parentElement.querySelector('[data-add]');
    if (!add) return;
    add.setAttribute('aria-disabled', select.value ? 'false' : 'true');
    add.dataset.variant = select.value;
  }

  async onClick(e) {
    const step = e.target.closest('[data-scroll]');
    if (step) {
      const list = this.querySelector('.sq-sug__list');
      const card = list?.querySelector('.sq-sug__item');
      if (list && card) list.scrollBy({ left: Number(step.dataset.scroll) * (card.offsetWidth + 10), behavior: 'smooth' });
      return;
    }

    const pending = e.target.closest('[data-add]');
    if (pending && (pending.getAttribute('aria-disabled') === 'true' || !pending.dataset.variant)) {
      pending.parentElement.querySelector('select')?.focus();
      this.announce('Choose a colour and size first.');
      return;
    }

    const button = e.target.closest('button[data-variant]');
    if (!button) return;

    button.disabled = true;
    const original = button.textContent;
    button.textContent = '…';

    try {
      const root = window.Shopify?.routes?.root || '/';
      const res = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: Number(button.dataset.variant), quantity: 1 }),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).description || 'Could not add');

      await this.refreshCart();
    } catch (err) {
      button.disabled = false;
      button.textContent = original;
      this.announce(err.message || 'Could not add that to your cart.');
    }
  }

  /** Re-renders whichever cart surface is on screen using Dawn's own sections. */
  async refreshCart() {
    const drawer = document.querySelector('cart-drawer');
    const root = window.Shopify?.routes?.root || '/';

    if (drawer && typeof drawer.getSectionsToRender === 'function') {
      const res = await fetch(`${root}?sections=${drawer.getSectionsToRender().map((s) => s.section || s.id).join(',')}`);
      const sections = await res.json();
      drawer.renderContents({ sections });
      return;
    }

    // Cart page: a full reload is the simplest correct way to refresh every total.
    window.location.reload();
  }

  announce(message) {
    const live = document.getElementById('CartDrawer-LiveRegionText') || document.getElementById('cart-live-region-text');
    if (live) live.textContent = message;
  }

  escape(value) {
    return String(value ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
  }
}

customElements.define('squish-cart-suggestions', SquishCartSuggestions);
})();
